import { useState, useCallback, useRef } from "react";
import { Megaphone, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button, cn, Input, Label, Switch } from "@cieda/djayma-ui";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useLocalCrud, generateId, LS_KEYS } from "./cms-store";
import { ConfirmDialog } from "./ConfirmDialog";
import { OfflineBadge } from "./OfflineBadge";
import type { Promotion, PromoFormData } from "./cms-types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getPromotionStatus(promo: Promotion): {
  readonly label: string;
  readonly className: string;
} {
  const now = new Date();
  const start = new Date(promo.start_date);
  const end = new Date(promo.end_date);

  if (now < start) {
    return {
      label: "\u00C0 venir",
      className: "bg-blue-100 text-blue-800 border-blue-200",
    };
  }
  if (now > end) {
    return {
      label: "Termin\u00E9e",
      className: "bg-stone-100 text-stone-600 border-stone-200",
    };
  }
  return {
    label: "En cours",
    className: "bg-emerald-100 text-emerald-800 border-emerald-200",
  };
}

// ---------------------------------------------------------------------------
// Empty form default
// ---------------------------------------------------------------------------

const EMPTY_PROMO_FORM: PromoFormData = {
  title: "",
  description: "",
  start_date: "",
  end_date: "",
  discount_percentage: 0,
  discount_type: "percent",
  target: "all",
  is_active: true,
};

// ---------------------------------------------------------------------------
// Promo form modal
// ---------------------------------------------------------------------------

function PromoFormModal({
  open,
  onClose,
  onSubmit,
  initialData,
  isSubmitting,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (data: PromoFormData) => void;
  readonly initialData: PromoFormData;
  readonly isSubmitting: boolean;
}) {
  const [form, setForm] = useState<PromoFormData>(initialData);
  const [dateError, setDateError] = useState<string | null>(null);

  const prevDataRef = useRef(initialData);
  if (prevDataRef.current !== initialData) {
    prevDataRef.current = initialData;
    setForm(initialData);
    setDateError(null);
  }

  const updateField = <K extends keyof PromoFormData>(
    key: K,
    value: PromoFormData[K],
  ) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (
        (key === "start_date" || key === "end_date") &&
        next.start_date &&
        next.end_date
      ) {
        if (new Date(next.end_date) <= new Date(next.start_date)) {
          setDateError(
            "La date de fin doit \u00EAtre post\u00E9rieure \u00E0 la date de d\u00E9but",
          );
        } else {
          setDateError(null);
        }
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }
    if (!form.start_date || !form.end_date) {
      toast.error("Les dates sont obligatoires");
      return;
    }
    if (new Date(form.end_date) <= new Date(form.start_date)) {
      toast.error(
        "La date de fin doit \u00EAtre post\u00E9rieure \u00E0 la date de d\u00E9but",
      );
      return;
    }
    onSubmit(form);
  };

  const isEditing = initialData.title !== "";

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={isEditing ? "Modifier la promotion" : "Nouvelle promotion"}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || dateError !== null}
          >
            {isSubmitting
              ? "Chargement..."
              : isEditing
                ? "Mettre \u00E0 jour"
                : "Cr\u00E9er"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="promo-title">Titre *</Label>
          <Input
            id="promo-title"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            placeholder="Titre de la promotion"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="promo-desc">Description</Label>
          <textarea
            id="promo-desc"
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="Description de la promotion"
            rows={3}
            className="flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="promo-start">Date de début *</Label>
            <Input
              id="promo-start"
              type="date"
              value={form.start_date}
              onChange={(e) => updateField("start_date", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="promo-end">Date de fin *</Label>
            <Input
              id="promo-end"
              type="date"
              value={form.end_date}
              onChange={(e) => updateField("end_date", e.target.value)}
            />
          </div>
        </div>

        {dateError && <p className="text-sm text-red-500">{dateError}</p>}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="promo-discount">Remise</Label>
            <div className="flex items-center gap-2">
              <Input
                id="promo-discount"
                type="number"
                min={0}
                value={form.discount_percentage}
                onChange={(e) =>
                  updateField(
                    "discount_percentage",
                    parseFloat(e.target.value) || 0,
                  )
                }
                className="flex-1"
              />
              <select
                value={form.discount_type}
                onChange={(e) =>
                  updateField(
                    "discount_type",
                    e.target.value as "percent" | "fixed",
                  )
                }
                className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              >
                <option value="percent">%</option>
                <option value="fixed">FCFA</option>
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="promo-target">Cible</Label>
            <select
              id="promo-target"
              value={form.target}
              onChange={(e) =>
                updateField("target", e.target.value as "all" | "segment")
              }
              className="flex h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              <option value="all">Tous les clients</option>
              <option value="segment">Segment spécifique</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="promo-active"
            checked={form.is_active}
            onCheckedChange={(val) => updateField("is_active", val)}
          />
          <Label htmlFor="promo-active">Promotion active</Label>
        </div>
      </form>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Promotions Tab
// ---------------------------------------------------------------------------

export function PromotionsTab() {
  const crud = useLocalCrud<Promotion>({
    queryKey: "cms-promotions",
    apiPath: "/api/cms/promos",
    lsKey: LS_KEYS.promotions,
    extractKey: "promotions",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Promotion | null>(null);

  const openCreateModal = useCallback(() => {
    setEditingPromo(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((promo: Promotion) => {
    setEditingPromo(promo);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingPromo(null);
  }, []);

  const handleSubmit = useCallback(
    (data: PromoFormData) => {
      if (editingPromo) {
        crud.updateItem({
          ...editingPromo,
          title: data.title,
          description: data.description || null,
          start_date: data.start_date,
          end_date: data.end_date,
          discount_percentage: data.discount_percentage || null,
          discount_type: data.discount_type,
          target: data.target,
          is_active: data.is_active,
        });
      } else {
        crud.createItem({
          id: generateId(),
          title: data.title,
          description: data.description || null,
          start_date: data.start_date,
          end_date: data.end_date,
          discount_percentage: data.discount_percentage || null,
          discount_type: data.discount_type,
          target: data.target,
          is_active: data.is_active,
          created_at: new Date().toISOString(),
        });
      }
      closeModal();
    },
    [editingPromo, crud, closeModal],
  );

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    crud.deleteItem(deleteTarget.id);
    setDeleteTarget(null);
  }, [deleteTarget, crud]);

  const formData: PromoFormData = editingPromo
    ? {
        title: editingPromo.title,
        description: editingPromo.description ?? "",
        start_date: editingPromo.start_date,
        end_date: editingPromo.end_date,
        discount_percentage: editingPromo.discount_percentage ?? 0,
        discount_type: editingPromo.discount_type,
        target: editingPromo.target,
        is_active: editingPromo.is_active,
      }
    : EMPTY_PROMO_FORM;

  const columns: readonly ColumnDef<Promotion>[] = [
    {
      id: "title",
      header: "Titre",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.title}</span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => {
        const { label, className } = getPromotionStatus(row);
        return (
          <span
            className={cn(
              "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
              className,
            )}
          >
            {label}
          </span>
        );
      },
    },
    {
      id: "discount",
      header: "Remise",
      cell: (row) => {
        if (!row.discount_percentage)
          return <span className="text-xs text-stone-400">\u2014</span>;
        return (
          <span className="text-sm font-medium text-stone-700">
            {row.discount_type === "fixed"
              ? `${new Intl.NumberFormat("fr-SN").format(row.discount_percentage)} FCFA`
              : `${row.discount_percentage}%`}
          </span>
        );
      },
    },
    {
      id: "target",
      header: "Cible",
      cell: (row) => (
        <span className="text-sm text-stone-500">
          {row.target === "all" ? "Tous" : "Segment"}
        </span>
      ),
    },
    {
      id: "start_date",
      header: "D\u00E9but",
      cell: (row) => (
        <span className="text-xs text-stone-500">
          {formatDate(row.start_date)}
        </span>
      ),
    },
    {
      id: "end_date",
      header: "Fin",
      cell: (row) => (
        <span className="text-xs text-stone-500">
          {formatDate(row.end_date)}
        </span>
      ),
    },
    {
      id: "is_active",
      header: "Actif",
      cell: (row) => (
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
            row.is_active
              ? "bg-emerald-100 text-emerald-700"
              : "bg-stone-100 text-stone-500",
          )}
        >
          {row.is_active ? "Actif" : "Inactif"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => openEditModal(row)}
            className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
            aria-label="Modifier"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="rounded-md p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-500"
            aria-label="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {crud.isOffline && <OfflineBadge />}
        </div>
        <Button onClick={openCreateModal} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Nouvelle promotion
        </Button>
      </div>

      {crud.items.length === 0 && !crud.isLoading ? (
        <EmptyState
          icon={<Megaphone className="h-6 w-6" />}
          title="Aucune promotion"
          description="Créez des promotions pour vos campagnes marketing."
          action={{ label: "Nouvelle promotion", onClick: openCreateModal }}
        />
      ) : (
        <AdminDataTable
          columns={columns}
          data={crud.items as Promotion[]}
          isLoading={crud.isLoading}
          emptyMessage="Aucune promotion trouvée."
        />
      )}

      <PromoFormModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialData={formData}
        isSubmitting={crud.isCreating || crud.isUpdating}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Supprimer la promotion"
        message={`Voulez-vous vraiment supprimer la promotion \u00AB ${deleteTarget?.title ?? ""} \u00BB ? Cette action est irr\u00E9versible.`}
        isLoading={crud.isDeleting}
      />
    </div>
  );
}
